import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { 
    LeakCheckForm, 
    CustomerInfoSection, 
    TankCheckSection, 
    RegulatorCheckSection, 
    PressureTestSection, 
    CustomSection, 
    CustomField, 
    FieldOption, 
    FieldType
} from '../../../entities/business/LeakCheckFormTemplate';
import { LeakCheckService, UpdateLeakCheckFormRequest } from '../../../services/business/LeakCheckService';
import { useAlert } from '../../../hooks/useAlert';

interface LeakCheckFormContextType {
    form: LeakCheckForm | null;
    setForm: React.Dispatch<React.SetStateAction<LeakCheckForm | null>>;
    saving: boolean;
    saveForm: () => Promise<boolean>;
    isDirty: boolean;
    resetDirtyState: () => void;
    
    // Basic form info
    updateName: (name: string) => void;
    toggleActive: () => void;
    
    // Customer info section
    toggleCustomerInfoField: (field: keyof CustomerInfoSection) => void;
    
    // Tank check section
    toggleTankCheckField: (field: keyof Omit<TankCheckSection, "customFields">) => void;
    
    // Regulator check section
    toggleRegulatorCheckField: (field: keyof Omit<RegulatorCheckSection, "customFields">) => void;
    
    // Pressure tests
    addPressureTest: () => void;
    removePressureTest: (index: number) => void;
    updatePressureTestType: (index: number, testType: string) => void;
    
    // Custom sections
    addCustomSection: () => void;
    removeCustomSection: (index: number) => void;
    updateCustomSection: (index: number, updates: Partial<CustomSection>) => void;
    reorderCustomSections: (sections: CustomSection[]) => void;
    
    // Custom fields
    addCustomFieldToSection: (sectionType: string, sectionIndex?: number) => void;
    removeCustomField: (sectionType: string, fieldIndex: number, sectionIndex?: number) => void;
    updateCustomField: (
        sectionType: string, 
        fieldIndex: number, 
        updates: Partial<CustomField>, 
        sectionIndex?: number
    ) => void;
    
    // Field options
    addFieldOption: (field: CustomField) => CustomField;
    updateFieldOption: (field: CustomField, optionIndex: number, label: string) => CustomField;
    removeFieldOption: (field: CustomField, optionIndex: number) => CustomField;
}

export const LeakCheckFormContext = createContext<LeakCheckFormContextType | undefined>(undefined);

export const useLeakCheckForm = (): LeakCheckFormContextType => {
    const context = useContext(LeakCheckFormContext);
    if (!context) {
        throw new Error('useLeakCheckForm must be used within a LeakCheckFormProvider');
    }
    return context;
};

interface LeakCheckFormProviderProps {
    children: ReactNode;
    initialForm: LeakCheckForm | null;
}

export const LeakCheckFormProvider: React.FC<LeakCheckFormProviderProps> = ({ 
    children, 
    initialForm 
}) => {
    const [form, setForm] = useState<LeakCheckForm | null>(initialForm);
    const [originalForm, setOriginalForm] = useState<string>(
        initialForm ? JSON.stringify(initialForm) : ''
    );
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const alert = useAlert();

    // Track dirty state whenever form changes
    useEffect(() => {
        if (!form) return;
        
        const currentFormStr = JSON.stringify(form);
        const originalFormStr = originalForm;
        
        // Compare the current form state with the original form state
        // We need to make sure we're comparing the actual content, not object references
        setIsDirty(currentFormStr !== originalFormStr);
    }, [form, originalForm]);

    // Reset the dirty state (used after saving)
    const resetDirtyState = () => {
        if (!form) return;
        setOriginalForm(JSON.stringify(form));
        setIsDirty(false);
    };

    // Save form changes
    const saveForm = async (): Promise<boolean> => {
        if (!form) return false;

        setSaving(true);
        try {
            const updateRequest: UpdateLeakCheckFormRequest = {
                id: form.id,
                name: form.name,
                isActive: form.isActive,
                customerInfo: form.customerInfo,
                tankCheck: form.tankCheck,
                pressureTests: form.pressureTests,
                regulatorCheck: form.regulatorCheck,
                customSections: form.customSections
            };

            const result = await LeakCheckService.updateForm(updateRequest);
            if (result.success) {
                setForm(result.data);
                resetDirtyState();
                alert.success("Form saved successfully");
                return true;
            } else if (result.validation) {
                alert.validation(result);
                return false;
            } else {
                alert.serverError(result);
                return false;
            }
        } catch (error) {
            alert.error("An error occurred while saving the form");
            return false;
        } finally {
            setSaving(false);
        }
    };

    // Basic form info updates
    const updateName = (name: string) => {
        if (!form) return;
        setForm({ ...form, name });
    };

    const toggleActive = () => {
        if (!form) return;
        setForm({ ...form, isActive: !form.isActive });
    };

    // Customer Info section handlers
    const toggleCustomerInfoField = (field: keyof CustomerInfoSection) => {
        if (!form) return;
        const updatedCustomerInfo = { 
            ...form.customerInfo, 
            [field]: !form.customerInfo[field] 
        };
        setForm({ ...form, customerInfo: updatedCustomerInfo });
    };

    // Tank Check section handlers
    const toggleTankCheckField = (field: keyof Omit<TankCheckSection, "customFields">) => {
        if (!form) return;
        const updatedTankCheck = { 
            ...form.tankCheck, 
            [field]: !form.tankCheck[field] 
        };
        setForm({ ...form, tankCheck: updatedTankCheck });
    };

    // Regulator Check section handlers
    const toggleRegulatorCheckField = (field: keyof Omit<RegulatorCheckSection, "customFields">) => {
        if (!form) return;
        const updatedRegulatorCheck = { 
            ...form.regulatorCheck, 
            [field]: !form.regulatorCheck[field] 
        };
        setForm({ ...form, regulatorCheck: updatedRegulatorCheck });
    };

    // Pressure Test section handlers
    const addPressureTest = () => {
        if (!form) return;
        
        const newPressureTest: PressureTestSection = {
            testType: "Standard Test",
            customFields: []
        };
        
        setForm({
            ...form,
            pressureTests: [...form.pressureTests, newPressureTest]
        });
    };

    const removePressureTest = (index: number) => {
        if (!form) return;
        
        const updatedTests = [...form.pressureTests];
        updatedTests.splice(index, 1);
        
        setForm({
            ...form,
            pressureTests: updatedTests
        });
    };

    const updatePressureTestType = (index: number, testType: string) => {
        if (!form) return;
        
        const updatedTests = [...form.pressureTests];
        updatedTests[index].testType = testType;
        
        setForm({
            ...form,
            pressureTests: updatedTests
        });
    };

    // Custom Sections handlers
    const addCustomSection = () => {
        if (!form) return;
        
        const newSection: CustomSection = {
            id: `section-${Date.now()}`,
            name: "New Section",
            order: form.customSections.length,
            multipleRows: true,
            fields: []
        };
        
        setForm({
            ...form,
            customSections: [...form.customSections, newSection]
        });
    };

    const removeCustomSection = (index: number) => {
        if (!form) return;
        
        const updatedSections = [...form.customSections];
        updatedSections.splice(index, 1);
        
        // Update order of remaining sections
        updatedSections.forEach((section, idx) => {
            section.order = idx;
        });
        
        setForm({
            ...form,
            customSections: updatedSections
        });
    };

    const updateCustomSection = (index: number, updates: Partial<CustomSection>) => {
        if (!form) return;
        
        const updatedSections = [...form.customSections];
        updatedSections[index] = { ...updatedSections[index], ...updates };
        
        setForm({
            ...form,
            customSections: updatedSections
        });
    };

    const reorderCustomSections = (sections: CustomSection[]) => {
        if (!form) return;

        // Update order of all sections
        const updatedSections = sections.map((section, idx) => ({
            ...section,
            order: idx
        }));

        setForm({
            ...form,
            customSections: updatedSections
        });
    };

    // Custom fields management
    const addCustomFieldToSection = (sectionType: string, sectionIndex: number = -1) => {
        if (!form) return;
        
        const newField: CustomField = {
            id: `field-${Date.now()}`,
            label: "New Field",
            type: FieldType.Text,
            isRequired: false,
            options: []
        };
        
        let updatedForm = { ...form };
        
        if (sectionType === "tankCheck") {
            updatedForm.tankCheck.customFields = [
                ...updatedForm.tankCheck.customFields, 
                newField
            ];
        } else if (sectionType === "regulatorCheck") {
            updatedForm.regulatorCheck.customFields = [
                ...updatedForm.regulatorCheck.customFields, 
                newField
            ];
        } else if (sectionType === "pressureTest" && sectionIndex !== -1) {
            const updatedTests = [...updatedForm.pressureTests];
            updatedTests[sectionIndex].customFields = [
                ...updatedTests[sectionIndex].customFields,
                newField
            ];
            updatedForm.pressureTests = updatedTests;
        } else if (sectionType === "customSection" && sectionIndex !== -1) {
            const updatedSections = [...updatedForm.customSections];
            updatedSections[sectionIndex].fields = [
                ...updatedSections[sectionIndex].fields,
                newField
            ];
            updatedForm.customSections = updatedSections;
        }
        
        setForm(updatedForm);
    };

    const removeCustomField = (sectionType: string, fieldIndex: number, sectionIndex: number = -1) => {
        if (!form) return;
        
        let updatedForm = { ...form };
        
        if (sectionType === "tankCheck") {
            const updatedFields = [...updatedForm.tankCheck.customFields];
            updatedFields.splice(fieldIndex, 1);
            updatedForm.tankCheck.customFields = updatedFields;
        } else if (sectionType === "regulatorCheck") {
            const updatedFields = [...updatedForm.regulatorCheck.customFields];
            updatedFields.splice(fieldIndex, 1);
            updatedForm.regulatorCheck.customFields = updatedFields;
        } else if (sectionType === "pressureTest" && sectionIndex !== -1) {
            const updatedTests = [...updatedForm.pressureTests];
            const updatedFields = [...updatedTests[sectionIndex].customFields];
            updatedFields.splice(fieldIndex, 1);
            updatedTests[sectionIndex].customFields = updatedFields;
            updatedForm.pressureTests = updatedTests;
        } else if (sectionType === "customSection" && sectionIndex !== -1) {
            const updatedSections = [...updatedForm.customSections];
            const updatedFields = [...updatedSections[sectionIndex].fields];
            updatedFields.splice(fieldIndex, 1);
            updatedSections[sectionIndex].fields = updatedFields;
            updatedForm.customSections = updatedSections;
        }
        
        setForm(updatedForm);
    };

    const updateCustomField = (
        sectionType: string, 
        fieldIndex: number, 
        updates: Partial<CustomField>, 
        sectionIndex: number = -1
    ) => {
        if (!form) return;
        
        let updatedForm = { ...form };
        
        if (sectionType === "tankCheck") {
            const updatedFields = [...updatedForm.tankCheck.customFields];
            updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...updates };
            updatedForm.tankCheck.customFields = updatedFields;
        } else if (sectionType === "regulatorCheck") {
            const updatedFields = [...updatedForm.regulatorCheck.customFields];
            updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...updates };
            updatedForm.regulatorCheck.customFields = updatedFields;
        } else if (sectionType === "pressureTest" && sectionIndex !== -1) {
            const updatedTests = [...updatedForm.pressureTests];
            const updatedFields = [...updatedTests[sectionIndex].customFields];
            updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...updates };
            updatedTests[sectionIndex].customFields = updatedFields;
            updatedForm.pressureTests = updatedTests;
        } else if (sectionType === "customSection" && sectionIndex !== -1) {
            const updatedSections = [...updatedForm.customSections];
            const updatedFields = [...updatedSections[sectionIndex].fields];
            updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...updates };
            updatedSections[sectionIndex].fields = updatedFields;
            updatedForm.customSections = updatedSections;
        }
        
        setForm(updatedForm);
    };

    // Field options helpers
    const addFieldOption = (field: CustomField): CustomField => {
        const newOption: FieldOption = { label: `Option ${field.options.length + 1}` };
        return {
            ...field,
            options: [...field.options, newOption]
        };
    };
    
    const updateFieldOption = (field: CustomField, optionIndex: number, label: string): CustomField => {
        const updatedOptions = [...field.options];
        updatedOptions[optionIndex] = { label };
        return {
            ...field,
            options: updatedOptions
        };
    };
    
    const removeFieldOption = (field: CustomField, optionIndex: number): CustomField => {
        const updatedOptions = [...field.options];
        updatedOptions.splice(optionIndex, 1);
        return {
            ...field,
            options: updatedOptions
        };
    };

    const value = {
        form,
        setForm,
        saving,
        saveForm,
        isDirty,
        resetDirtyState,
        updateName,
        toggleActive,
        toggleCustomerInfoField,
        toggleTankCheckField,
        toggleRegulatorCheckField,
        addPressureTest,
        removePressureTest,
        updatePressureTestType,
        addCustomSection,
        removeCustomSection,
        updateCustomSection,
        reorderCustomSections,
        addCustomFieldToSection,
        removeCustomField,
        updateCustomField,
        addFieldOption,
        updateFieldOption,
        removeFieldOption
    };

    return (
        <LeakCheckFormContext.Provider value={value}>
            {children}
        </LeakCheckFormContext.Provider>
    );
};